import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';

import { getClientName } from 'components/roster-portal/activity-planning/utils';
import { queries } from 'RestAPIs/rapini';
import { ControlledSearch } from 'components/shared/ControlledFields';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import EmploymentSelect from './EmploymentSelect';

import type {
  ClientResponse,
  EmployeeResponse,
  MatchCandidateResponse
} from 'generated/index';
import type { Control } from 'react-hook-form';
import type { Event } from 'components/roster-portal/activity-planning/types';
import type { FieldKey } from '../input-field-grants';
import type { OrganizationalUnit } from 'components/types';

type Props = {
  enable?: boolean;
  candidateEmployment?: MatchCandidateResponse | null;
  control: Control<any>;
  event?: Event;
  client?: ClientResponse | null;
  employee?: EmployeeResponse | null;
  organizationalUnit?: OrganizationalUnit;
  hasFieldGrant: (key: FieldKey) => boolean;
  handleEmployeeSelect: (employee: EmployeeResponse | undefined | null) => void;
  handleCandidateEmploymentSelect?: (
    candidate: MatchCandidateResponse | undefined | null
  ) => void;
};

const WhoSection = ({
  enable,
  control,
  client,
  employee,
  candidateEmployment,
  event,
  organizationalUnit: _organizationalUnit,
  hasFieldGrant,
  handleEmployeeSelect,
  handleCandidateEmploymentSelect
}: Props) => {
  const { t } = useTranslation();
  const [isEmploymentSelectOpen, setIsEmploymentSelectOpen] = useState(false);

  if (!enable) return null;

  const linkedEmployee = event?.employees?.[0];
  const hasChangedEmployment = !!candidateEmployment;

  const employeeName = linkedEmployee?.nameInformal ?? employee?.nameInformal;

  const clientChip = client ? (
    <Chip size="small" label={getClientName(client)} />
  ) : null;

  const employeeChip = employeeName ? (
    <Chip size="small" label={employeeName} />
  ) : null;

  const whoPreview = (
    <Box display="flex" width="100%" alignItems="start" gap={1}>
      {clientChip}
      {employeeChip}
    </Box>
  );

  return (
    <MultiStepForm.Section
      title="Wie"
      isEditable={hasFieldGrant('WHO_IS_EDITABLE')}
      isPreview={
        hasFieldGrant('WHO_IS_PREVIEW') && !hasFieldGrant('WHO_IS_EDITABLE')
      }
      validateFields={['employee']}
      preview={whoPreview}
    >
      {hasFieldGrant('CLIENT_PREVIEW') && client && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('client')}
          </Typography>
          <Box display="flex" alignItems="start">
            {clientChip}
          </Box>
        </Box>
      )}

      {hasFieldGrant('CLIENT') && (
        <ClientSelect control={control} client={client} />
      )}

      {hasFieldGrant('EMPLOYEE') && (
        <EmployeeSelect
          control={control}
          employee={employee}
          handleSelect={handleEmployeeSelect}
        />
      )}

      {hasFieldGrant('EMPLOYEE_PREVIEW') && employeeName && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('employee')}
          </Typography>
          {employeeChip}
        </Box>
      )}

      {hasFieldGrant('EMPLOYMENT') && (
        <Box>
          {!isEmploymentSelectOpen &&
          !hasChangedEmployment &&
          linkedEmployee ? (
            <>
              <Typography variant="caption" color="text.secondary">
                {t('employee')}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip size="small" label={linkedEmployee.nameInformal} />
                <IconButton
                  size="small"
                  onClick={() => setIsEmploymentSelectOpen(true)}
                >
                  <Icon type={'pen' as const} fontSize="inherit" />
                </IconButton>
              </Box>
            </>
          ) : (
            <EmploymentSelect
              autoFocus={isEmploymentSelectOpen}
              control={control}
              event={event}
              candidateEmployment={candidateEmployment}
              handleSelect={handleCandidateEmploymentSelect}
            />
          )}
        </Box>
      )}
    </MultiStepForm.Section>
  );
};

const ClientSelect = ({
  control,
  client
}: {
  control: Control<any>;
  client?: ClientResponse | null;
  handleSelect?: (employee: EmployeeResponse | null) => void;
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState<string>('');

  const { data: clients, isLoading } = queries.useGetClients(
    undefined,
    query?.toLowerCase(),
    //@ts-expect-error
    ['VV'].join(','),
    { enabled: !!query?.toLowerCase() }
  );

  const options =
    clients?.map((client) => ({
      ...client,
      name: `${client.number} - ${getClientName(client)}`,
      sortName: `${client.lastName ?? ''} ${client.firstName ?? ''}`
    })) ?? [];

  return (
    <ControlledSearch<ClientResponse>
      name="client"
      control={control}
      idProperty="id"
      label={t('client')}
      labelProperty="name"
      noOptionsText={t('searchClient')}
      loading={isLoading}
      fullWidth
      debounceInMS={500}
      options={options}
      value={client}
      onQueryChange={setQuery}
    />
  );
};

const EmployeeSelect = ({
  control,
  employee,
  handleSelect
}: {
  control: Control<any>;
  employee?: EmployeeResponse | null;
  handleSelect?: (employee: EmployeeResponse | null) => void;
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState<string>('');
  const { data: employees, isLoading } = queries.useGetEmployees(
    undefined,
    undefined,
    query?.toLowerCase(),
    {
      enabled: !!query?.toLowerCase()
    }
  );

  return (
    <ControlledSearch
      name="employee"
      control={control}
      idProperty="entityId"
      label={t('employee')}
      labelProperty="label"
      noOptionsText={t('searchEmployee')}
      loading={isLoading}
      fullWidth
      debounceInMS={500}
      options={(employees || []).map((employee: EmployeeResponse) => ({
        label: employee?.nameInformal || '',
        ...employee
      }))}
      value={
        employee && {
          label: employee?.nameInformal || '',
          ...employee
        }
      }
      onQueryChange={setQuery}
      onChange={handleSelect}
    />
  );
};

export default WhoSection;
